import React, { useState } from 'react';
import { excelHeaders, getHeaders } from '.';
import { IListingData } from '@models/listing';
import { downloadExcelFile } from 'src/pages/policy-management/excel/new-download-json-excel-service';
import ListingTableWithPagination from '@components/table/with-pagination/ListingTableWithPagination';
import { ActionTarget, IEnhancedMenuItem } from '@components/table/types';
import {
  getMedicalGroupPolicyList,
  getMedicalGroupPolicyWithoutPaginationList,
} from '../../../../queries';
import { useLazyQuery } from '@apollo/client';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_TEXT } from '@utils';
import {
  mapToMedicalPolicyListData,
  mapToMedicalPolicyListDataToExcel,
} from '../../../../group-utils';
import { enumListAsRecordObject } from '@utils/graph-utils';
import AddMedicalCertificateDrawer from '../medical-certificate-drawer/AddMedicalCertificateDrawer';
import { IAbstractRecord } from '@models';

interface IMedicalGroupCertificateTableWidgetProps {
  className?: string;
  entityId: string;
  currencySymbol: string;
  planDetails: IAbstractRecord;
  onUpdateSuccess?: () => void;
}

export const MedicalGroupCertificateTableWidget: React.FC<
  IMedicalGroupCertificateTableWidgetProps
> = ({ entityId, currencySymbol, planDetails }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [addDocumentDrawerOpen, setAddDocumentDrawerOpen] =
    useState<boolean>(false);
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [getData] = useLazyQuery(getMedicalGroupPolicyList());
  const [getDownloadData] = useLazyQuery(
    getMedicalGroupPolicyWithoutPaginationList()
  );

  const tableAction: IEnhancedMenuItem[] = [
    {
      title: 'Download',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick: () => {
        getDownloadData({
          variables: {
            id: entityId,
            type: 'CERTIFICATE',
          },
        })
          .then((result) => {
            const data = mapToMedicalPolicyListDataToExcel(result.data);
            downloadExcelFile({
              data: data,
              filename: 'Policy Certificates',
              headers: excelHeaders(currencySymbol),
            });
          })
          .catch(() => {
            toast.error(
              <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
            );
          });
      },
      target: ActionTarget.Entity,
      rowIndependant: true,
      disabled: false,
      name: 'download',
    },
    {
      title: 'Add Certificates',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick: () => {
        setAddDocumentDrawerOpen(true);
      },
      target: ActionTarget.Entity,
      rowIndependant: true,
      disabled: false,
      name: 'addCertificates',
    },
  ];

  const loadData = async (
    currentPage: number,
    pageSize = tableData.pageSize,
    searchKey?: string,
    order?: string,
    orderBy?: string
  ) => {
    setLoading(true);

    try {
      const result = await getData({
        variables: {
          id: entityId,
          currentPage: currentPage,
          currentPageSize: pageSize,
          KeywordSearch: searchKey,
          OrderBy: orderBy
            ? {
                Attribute: orderBy,
                Descending: order === 'desc',
              }
            : null,
          type: 'CERTIFICATE',
        },
        errorPolicy: 'all',
      });

      if (result.error) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        return;
      }

      const data = result.data;
      const statuses = enumListAsRecordObject(
        data?.Production_PolicyStatuses?.enumValues
      );

      const newTableData = mapToMedicalPolicyListData(data, statuses);
      setTableData({
        ...newTableData,
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (
    page: number,
    searchKey?: string,
    rowsPerPage?: number,
    order?: string,
    orderBy?: string
  ) => {
    await loadData(
      page,
      rowsPerPage || tableData.pageSize,
      searchKey,
      order,
      orderBy
    );
  };

  return (
    <>
      <ListingTableWithPagination
        disableSelection
        actions={tableAction}
        name={'table'}
        inlineTitle={'Certificates'}
        inline={true}
        headers={getHeaders(currencySymbol)}
        data={tableData}
        loader={loading}
        onPageChange={handlePageChange}
      />
      {addDocumentDrawerOpen && (
        <AddMedicalCertificateDrawer
          id={entityId}
          open={addDocumentDrawerOpen}
          onClose={() => {
            setAddDocumentDrawerOpen(false);
          }}
          onSuccess={() => {
            setAddDocumentDrawerOpen(false);
            handlePageChange(1);
          }}
          planDetails={planDetails}
        />
      )}
    </>
  );
};
